import { Typography } from '@mui/material';
import React from 'react';
import ButtonWithIcon from '../components/ButtonWithIcon';
import UploadFile from '../components/upload/UploadFile';
import { FormContainer } from '../form/FormContainer';
import FormItem from '../form/FormItem';
import FormPanel from '../form/FormPanel';
import FormWidget from '../form/FormWidget';
import DropzoneInformations from '../form/widgets/DropzoneInformations';
import I18n from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import AttachmentUploadIcon from './AttachmentUploadIcon';

type AttachmentsEditorFormPanelProps = {
    insideSidePanel?: boolean;
    hideDropzoneState: boolean;
    dropzoneRef: any;
    dropzoneKey: number;
    entityReference?: any;
    maxFileSize?: number;
    acceptedFiles?: Array<string>;
    filesLimit?: number;
    onChange: (saveFile: any) => void;
    customDeleteFromPreview: boolean;
    saveFiles: any[];
    disableDropzone?: boolean;
    hideButtonsAndInfo: boolean;
    setIsUploading: (val: boolean) => void;
    setSaveFiles: React.Dispatch<React.SetStateAction<any[]>>;
    createAttachmentInput: (file: File) => any;
    defaultMandatoryTags: any[];
    handleOpenLinkDialog: () => void;
    enableLinkAttachment?: boolean;
    leftAlignContent?: boolean;
    disableButtons?: boolean;
    autoOpenFilePickerTrigger?: number;
    onAutoOpenFilePickerHandled?: () => void;
};

export default ({
    insideSidePanel,
    hideDropzoneState,
    dropzoneRef,
    dropzoneKey,
    entityReference,
    maxFileSize,
    acceptedFiles,
    filesLimit,
    onChange,
    customDeleteFromPreview,
    saveFiles,
    disableDropzone,
    hideButtonsAndInfo,
    setIsUploading,
    setSaveFiles,
    createAttachmentInput,
    defaultMandatoryTags,
    handleOpenLinkDialog,
    enableLinkAttachment = true,
    leftAlignContent = false,
    disableButtons = false,
    autoOpenFilePickerTrigger,
    onAutoOpenFilePickerHandled
}: AttachmentsEditorFormPanelProps) => (
    <FormPanel
        sx={{
            flex: 1,
            p: 0,
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        }}
    >
        <FormContainer>
            <FormItem quarter={insideSidePanel} gap={0} sx={{ p: 0, minHeight: '100px' }} hidden={hideDropzoneState}>
                <FormWidget
                    dropzoneRef={dropzoneRef}
                    dropzoneKey={dropzoneKey}
                    dropzoneText={
                        <Typography fontSize={'12px'} fontWeight={'400'}>
                            <I18n token="attachment.dropzone.text" />
                            <br />
                            <I18n token="common.or.label" />
                        </Typography>
                    }
                    name="attachments"
                    component="Dropzone"
                    onChange={onChange}
                    entityReference={entityReference}
                    insideSidePanel={insideSidePanel}
                    maxFileSize={maxFileSize}
                    acceptedFiles={acceptedFiles}
                    filesLimit={filesLimit}
                    showPreviewsInDropzone={false}
                    customDeleteFromPreview={customDeleteFromPreview}
                    saveFiles={saveFiles}
                    attachmentsToEntity
                    dropzoneSx={{
                        border: 'none',
                        boxShadow: 'none',
                        background: 'transparent',
                        fontSize: '12px',
                        '& svg.MuiSvgIcon-root': {
                            display: 'none'
                        }
                    }}
                    disableDropzone={disableDropzone}
                    hideDropzoneInformations
                    disableOnClickUpload
                />
            </FormItem>
            <FormItem
                hidden={hideButtonsAndInfo}
                sx={{ gap: 1, justifyContent: leftAlignContent ? 'flex-start' : 'center' }}
            >
                <UploadFile
                    UploadIconComponent={AttachmentUploadIcon}
                    setIsUploading={setIsUploading}
                    entityId={entityReference?.id || ''}
                    multiple
                    acceptedFiles={acceptedFiles?.join(',')}
                    onUploadComplete={(files, references) => {
                        setSaveFiles((prevFiles) => [
                            ...prevFiles,
                            ...files.map((file, index) => ({
                                ...createAttachmentInput(file),
                                ...(file.linkUrl ? { linkUrl: file.linkUrl } : {}),
                                entityReference: references[index],
                                tagSet: defaultMandatoryTags || []
                            }))
                        ]);
                    }}
                    disabled={disableButtons}
                    autoOpenFilePickerTrigger={autoOpenFilePickerTrigger}
                    onAutoOpenFilePickerHandled={onAutoOpenFilePickerHandled}
                />
                {enableLinkAttachment && (
                    <ButtonWithIcon
                        label={<I18n token="label.link" />}
                        onClick={handleOpenLinkDialog}
                        Icon={Icons.AddButton}
                        fontSize="small"
                        sx={{ width: 'fit-content' }}
                        disabled={disableButtons}
                    />
                )}
            </FormItem>
            <FormItem hidden={hideButtonsAndInfo}>
                <DropzoneInformations
                    containerSx={{ gap: 1, alignItems: leftAlignContent ? 'flex-start' : 'center' }}
                    maxFileSize={maxFileSize}
                    filesLimit={filesLimit}
                />
            </FormItem>
        </FormContainer>
    </FormPanel>
);
